import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function AudioSettingsFields({ audio, setAudio, idPrefix = "audio" }) {
  if (!audio) return null;
  const set = (k, v) => setAudio({ ...audio, [k]: v });
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="text-slate-300">Codecs</Label>
        <div className="flex flex-wrap gap-4">
          {["ulaw", "alaw", "gsm", "opus"].map((c) => (
            <label key={c} className="flex items-center gap-1.5 text-sm text-slate-300">
              <input type="checkbox" data-testid={`${idPrefix}-codec-${c}-checkbox`}
                checked={audio.codecs.includes(c)}
                onChange={(e) => set("codecs", e.target.checked ? [...audio.codecs, c] : audio.codecs.filter((x) => x !== c))}
                className="accent-emerald-500" />
              <span className="font-mono">{c}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          ulaw/alaw (G.711): uncompressed, best quality, no transcoding. gsm: low bandwidth but robotic.
          opus: HD audio but heavy CPU on a small VPS (crackle risk).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Volume boost from each phone (dB)</Label>
          <Input type="number" min="-10" max="10" data-testid={`${idPrefix}-volume-rx-input`}
            value={audio.volume_rx} onChange={(e) => set("volume_rx", Number(e.target.value))}
            className="bg-[#0A0D14] border-[#1E2638]" />
          <p className="text-xs text-slate-500">Raises what the OTHER side hears. 0 = off. Try 3–6.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Volume boost to each phone (dB)</Label>
          <Input type="number" min="-10" max="10" data-testid={`${idPrefix}-volume-tx-input`}
            value={audio.volume_tx} onChange={(e) => set("volume_tx", Number(e.target.value))}
            className="bg-[#0A0D14] border-[#1E2638]" />
          <p className="text-xs text-slate-500">Raises what each phone's own earpiece plays. 0 = off.</p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" data-testid={`${idPrefix}-agc-enabled-checkbox`}
            checked={audio.agc_enabled} onChange={(e) => set("agc_enabled", e.target.checked)}
            className="accent-emerald-500" />
          Automatic gain control (fixes "one side is quiet")
        </label>
        {audio.agc_enabled && (
          <div className="flex items-center gap-3">
            <Input type="number" min="1000" max="30000" step="500" data-testid={`${idPrefix}-agc-level-input`}
              value={audio.agc_level} onChange={(e) => set("agc_level", Number(e.target.value))}
              className="bg-[#0A0D14] border-[#1E2638] w-32" />
            <span className="text-xs text-slate-500">Target loudness — default 8000, louder = higher (clipping above ~12000).</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" data-testid={`${idPrefix}-denoise-checkbox`}
            checked={audio.denoise} onChange={(e) => set("denoise", e.target.checked)}
            className="accent-emerald-500" />
          Noise suppression (wind / background hiss)
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" data-testid={`${idPrefix}-echo-cancel-checkbox`}
            checked={audio.echo_cancel} onChange={(e) => set("echo_cancel", e.target.checked)}
            className="accent-emerald-500" />
          Echo cancellation
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Jitter buffer (fixes crackle)</Label>
          <select data-testid={`${idPrefix}-jitterbuffer-select`} value={audio.jitterbuffer}
            onChange={(e) => set("jitterbuffer", e.target.value)}
            className="w-full bg-[#0A0D14] border border-[#1E2638] rounded-md text-slate-200 text-sm h-9 px-2">
            <option value="adaptive">Adaptive (recommended)</option>
            <option value="fixed">Fixed size</option>
            <option value="off">Off</option>
          </select>
          {audio.jitterbuffer === "fixed" && (
            <div className="flex items-center gap-3 pt-1">
              <Input type="number" min="40" max="400" data-testid={`${idPrefix}-jitterbuffer-ms-input`}
                value={audio.jitterbuffer_ms} onChange={(e) => set("jitterbuffer_ms", Number(e.target.value))}
                className="bg-[#0A0D14] border-[#1E2638] w-28" />
              <span className="text-xs text-slate-500">ms — 100–200 smooths bad links but adds delay</span>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">RTP keepalive (seconds)</Label>
          <Input type="number" min="0" max="60" data-testid={`${idPrefix}-keepalive-input`}
            value={audio.rtp_keepalive} onChange={(e) => set("rtp_keepalive", Number(e.target.value))}
            className="bg-[#0A0D14] border-[#1E2638]" />
          <p className="text-xs text-slate-500">Keeps NAT ports open on silent lines. 0 = off, 15 recommended.</p>
        </div>
      </div>
    </div>
  );
}
